<template>
  <div
    ref="moveDOM"
    class="floating-call-btn"
    @mousedown="onMousedown"
    @mousemove="onMousemove"
    @mouseup="onMouseup"
    @mouseleave="onMouseleave"
  >
    <div class="floating-call-btn__text">
      <span>自定义拨打</span>
      <span>电话</span>
    </div>
  </div>

  <a-modal
    v-model:visible="visible"
    title="外呼电话"
    :footer="null"
    :width="440"
    :mask-closable="false"
    @cancel="close"
  >
    <div class="floating-call-modal">
      <a-input
        v-model:value="phone"
        addon-before="+86"
        placeholder="请输入电话号码"
        @pressEnter="handleCall"
      />
      <div class="floating-call-modal__actions">
        <a-button @click="close">取消</a-button>
        <a-button type="primary" :loading="loading" @click="handleCall">去拨打</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, createVNode } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { useMoveDom } from '@/components/PatientFile/hooksMoveDOM'
import { callOut } from '@/utils/Tccc'
import { roleMap } from '@/components/Tccc/config'
import { identifyCalleeByPhone } from '@/api/login'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
import { updateMatters } from '@/api/HealthRecord'

const visible = ref(false)
const phone = ref('')
const loading = ref(false)

const open = () => {
  phone.value = ''
  visible.value = true
}

const close = () => {
  visible.value = false
  phone.value = ''
  loading.value = false
}

// 复用项目已有的拖拽 hook（PatientFile/hooksMoveDOM）
const { moveDOM } = useMoveDom()

// 区分「单击打开」与「拖拽移动」：按下记录起点，移动超阈值视为拖拽，松开时不打开弹窗
const pressing = ref(false)
const startX = ref(0)
const startY = ref(0)
const moved = ref(false)
const onMousedown = e => {
  pressing.value = true
  moved.value = false
  startX.value = e.clientX
  startY.value = e.clientY
}
const onMousemove = e => {
  if (!pressing.value) return
  if (Math.abs(e.clientX - startX.value) > 5 || Math.abs(e.clientY - startY.value) > 5) {
    moved.value = true
  }
}
const onMouseup = () => {
  if (!pressing.value) return
  pressing.value = false
  if (!moved.value) open()
}
const onMouseleave = () => {
  // 拖拽中鼠标移出元素，放弃本次点击判定，避免松手在元素外误触发打开
  if (pressing.value) {
    pressing.value = false
    moved.value = false
  }
}

const handleCall = async () => {
  const raw = (phone.value || '').trim().replace(/\s/g, '')
  if (!raw) {
    return message.warning('请输入电话号码')
  }
  // 输入框已带 +86 前缀，兼容用户多输一次 +86/0086/86 的情况
  const digits = raw.replace(/^(?:\+|00)?86/, '')
  if (!/^\d+$/.test(digits)) {
    return message.warning('电话号码只能包含数字')
  }
  // 点击即识别被叫：把识别结果带进二次确认弹窗
  loading.value = true
  try {
    const data = await identifyCalleeByPhone({ phone: digits })

    const calleePhone = data.phone || digits
    const content = data.name
      ? `确定给${data.userTypeName || '用户'}-${data.name}（${calleePhone}）拨打电话吗？`
      : `确定给未知用户（${calleePhone}）拨打电话吗？`
    Modal.confirm({
      icon: createVNode(ExclamationCircleOutlined),
      title: '提示',
      content: content,
      okText: '拨打',
      cancelText: '取消',
      onOk: () => doCall(data, digits)
    })
  } catch (e) {
    message.warning('号码识别失败，暂不支持拨打')
  } finally {
    loading.value = false
  }
}

// 二次确认后执行：用识别到的被叫信息发起外呼
const doCall = async (data, digits) => {
  loading.value = true
  try {
    const p = {
      // 被叫类型：-1预建档患者，0未知用户，1患者，2医生，3BD
      calleeType: data.calleeType,
      // 被叫用户标识（number|null，未知用户为 null）
      calleeUcUserId: data.calleeUcUserId,
      // 患者类型：0预患者，1正式患者（医生/BD/未知为 null）
      patientType: data.patientType,
      workOrderIds: [],
      phone: data.phone || digits
    }
    await callOut(p, {
      // 优先用接口返回的 userTypeName（中文；roleMap 中 0=系统 错位，故兜底在其后）
      userType: data.userTypeName || roleMap[data.calleeType] || '',
      name: data.name || ''
    }).then(result => {
      if (result.cdrId) {
        const params = {
          cdrId: result.cdrId,
          serviceMatters: '',
          mattersRemark: ''
        }
        updateMatters(params)
      }
    })
    close()
  } catch (e) {
    message.warning('号码识别失败，暂不支持拨打')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped lang="scss">
.floating-call-btn {
  position: fixed;
  right: 12px;
  bottom: 100px;
  z-index: 1000;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  user-select: none;
  transition: box-shadow 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
  }

  &__text {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.2;

    span {
      font-size: 11px;
      color: #595959;
      white-space: nowrap;
    }
  }
}

.floating-call-modal {
  padding: 8px 4px;

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
  }
}
</style>
